import React from 'react';
import AttachmentPreview from './AttachmentPreview';
import AIEditModal from './AIEditModal';
import ImageCropModal from './ImageCropModal';
import AttachmentPreviewSection from './AttachmentPreviewSection';
import AttachmentInfo from './AttachmentInfo';
import { useAttachmentState } from './hooks/useAttachmentState';
import { useFileUtils } from './hooks/useFileUtils';
import { useAttachmentActions } from './hooks/useAttachmentActions';
import './AttachmentCard.scss';

const AttachmentCard = ({
    attachment,
    editable = false,
    onDelete,
    onOrderUpdate,
    showOrderControls = false,
    canMoveUp = false,
    canMoveDown = false,
    showInfo = true,
    className = "",
    thumbnailSize = null, // 80, 120, 300, or null for full size
    onEditComplete = null, // Unified callback for AI edit and crop completion
    onImageClick = null // Custom image click handler for gallery preview
}) => {
    // State management - hooks must be called before any early returns
    const state = useAttachmentState();
    const {
        showPreview,
        setShowPreview,
        imageLoaded,
        setImageLoaded,
        showAIEditModal,
        setShowAIEditModal,
        showCropModal,
        setShowCropModal,
        isAIEditing,
        setIsAIEditing,
        isCropping,
        setIsCropping,
        imageTimestamp,
        setImageTimestamp
    } = state;

    // File utilities - must be called before early return
    const fileUtils = useFileUtils(attachment?.file, imageTimestamp, thumbnailSize);
    const {
        getFileUrl,
        getFileUrlForCrop,
        isImage,
        isPDF,
        formatFileSize,
        getFileIcon,
        getFileType
    } = fileUtils;

    // Action handlers - must be called before early return
    const actions = useAttachmentActions(
        attachment,
        {
            setIsAIEditing,
            setIsCropping,
            setShowAIEditModal,
            setShowCropModal,
            setImageTimestamp,
            setImageLoaded
        },
        {
            onAIEditComplete: onEditComplete,
            onCropComplete: onEditComplete,
            onDelete,
            onOrderUpdate
        }
    );

    // Early return after all hooks have been called
    if (!attachment || !attachment.file) {
        return null;
    }

    const { file } = attachment;

    const {
        handleAIEditSubmit,
        handleCropSubmit,
        handleMoveUp,
        handleMoveDown,
        handleDelete,
        canAIEdit,
        canCrop
    } = actions;

    // Event handlers
    const openPreview = () => setShowPreview(true);
    const closePreview = () => setShowPreview(false);

    // Handle image click - use custom handler if provided, otherwise default preview
    const handleImageClick = () => {
        if (onImageClick && isImage()) {
            onImageClick();
        } else {
            openPreview();
        }
    };

    const handleAIEdit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('AI Edit button clicked');
        setShowAIEditModal(true);
    };

    const handleCrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Crop button clicked');
        setShowCropModal(true);
    };

    const handleAIEditCancel = () => {
        if (!isAIEditing) {
            setShowAIEditModal(false);
        }
    };

    const handleCropCancel = () => {
        if (!isCropping) {
            setShowCropModal(false);
        }
    };

    const handleCropSubmitWrapper = (cropParams) => {
        return handleCropSubmit(cropParams, getFileUrlForCrop);
    };

    return (
        <>
            <div className={`attachment-card ${className}`}>
                {/* Preview Section */}
                <AttachmentPreviewSection
                    file={file}
                    fileUrl={getFileUrl(true)}
                    isImage={isImage()}
                    getFileIcon={getFileIcon}
                    getFileType={getFileType}
                    imageLoaded={imageLoaded}
                    setImageLoaded={setImageLoaded}
                    onPreviewClick={handleImageClick}
                />

                {/* Info Section */}
                {showInfo && (
                    <AttachmentInfo
                        attachment={attachment}
                        file={file}
                        formatFileSize={formatFileSize}
                        editable={editable}
                        showOrderControls={showOrderControls}
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        // Actions props
                        canCrop={canCrop(editable, isImage())}
                        canAIEdit={canAIEdit(editable, isImage())}
                        isCropping={isCropping}
                        isAIEditing={isAIEditing}
                        onCrop={handleCrop}
                        onAIEdit={handleAIEdit}
                        onDelete={handleDelete}
                    />
                )}
            </div>

            {/* Preview Modal - only show if no custom image click handler */}
            {showPreview && !onImageClick && (
                <AttachmentPreview
                    attachment={attachment}
                    fileUrl={getFileUrl()}
                    onClose={closePreview}
                />
            )}

            {/* AI Edit Modal */}
            {showAIEditModal && (
                <AIEditModal
                    isOpen={showAIEditModal}
                    onClose={handleAIEditCancel}
                    onConfirm={handleAIEditSubmit}
                    attachment={attachment}
                    isLoading={isAIEditing}
                />
            )}

            {/* Image Crop Modal */}
            {showCropModal && (
                <ImageCropModal
                    isOpen={showCropModal}
                    onClose={handleCropCancel}
                    onConfirm={handleCropSubmitWrapper}
                    attachment={attachment}
                    isLoading={isCropping}
                />
            )}
        </>
    );
};

export default AttachmentCard;
